const FUNNEL_PROFILE_ID_KEY = "funnel_external_profile_id";
const FUNNEL_EMAIL_KEY = "funnel_email";

const readLocalStorage = (key: string) => {
  try {
    return localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
};

const emptySubscription = {
  hasPremium: false,
  isCanceled: null,
  dateEnd: null,
  plan: null,
  isNewCustomer: false,
  subscriptionAfterTrail: false,
  source: "client_fallback",
};

// The funnel profile id is a UUID that keys the Adapty profile created during
// the /start quiz. It belongs only to the person who completed that funnel, and
// it isn't tied to the Supabase account. Only trust it for entitlement lookup
// when the funnel email matches the signed-in email; otherwise a funnel purchase
// would leak premium to any other account signing in from the same browser.
const getFunnelProfileId = (email: string) => {
  const profileId = readLocalStorage(FUNNEL_PROFILE_ID_KEY);
  if (!profileId) {
    return undefined;
  }

  const funnelEmail = (readLocalStorage(FUNNEL_EMAIL_KEY) || "").trim().toLowerCase();
  const normalizedEmail = (email || "").trim().toLowerCase();

  if (!funnelEmail || funnelEmail !== normalizedEmail) {
    return undefined;
  }

  return profileId;
};

export const getSubscriptionInfo = async (email: string, userId?: string) => {
  const data = {
    email,
    userId,
    externalProfileId: getFunnelProfileId(email),
  };

  const subscription = await fetch("/.netlify/functions/get-subscription-status", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then(async (res) => {
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(payload?.error || "Subscription status request failed");
      }
      return payload || emptySubscription;
    })
    .catch((error) => {
      console.error("Unable to load subscription status", error);
      return emptySubscription;
    });

  const dateEndMs = subscription.dateEnd ? subscription.dateEnd * 1000 : null;
  const hasPremium =
    Boolean(subscription.hasPremium) ||
    Boolean(subscription.dateEnd && dateEndMs && dateEndMs > Date.now());
  const { isCanceled, plan, isNewCustomer, subscriptionAfterTrail } = subscription;

  return {
    isNewSubscriber: isNewCustomer,
    hasPremium,
    subscriptionEnd: dateEndMs,
    isCanceled,
    plan,
    subscriptionAfterTrail,
    subscriptionSource: subscription.source,
  };
};
